import type React from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Autocomplete, Box, InputAdornment, TextField, Typography} from '@mui/material';
import {Search} from '@mui/icons-material';
import {matchCategories, searchIndex, type CategoryHit} from '../domain/categorySearch';
import {useExploreQuery} from '../hooks/useCategoryProfileQueries';

type Props = {databagId: number; onOpen: (path: string) => void};

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

/** "Search or jump to…": find any category of the dataset and open its profile. Ctrl K / ⌘ K focuses it. */
export const CategorySearch: React.FC<Props> = ({databagId, onOpen}) => {
    const {data} = useExploreQuery(databagId);
    const index = useMemo(() => searchIndex(data?.tree ?? []), [data]);
    const [query, setQuery] = useState('');
    const input = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                input.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return (
        <Autocomplete<CategoryHit, false, false, false>
            size="small" sx={{width: {xs: '100%', md: 440}}}
            options={matchCategories(index, query)} filterOptions={x => x}
            getOptionLabel={o => o.name} isOptionEqualToValue={(a, b) => a.path === b.path}
            inputValue={query} onInputChange={(_, v) => setQuery(v)}
            value={null} blurOnSelect clearOnBlur autoHighlight
            onChange={(_, hit) => {
                if (hit) {
                    setQuery('');
                    onOpen(hit.path);
                }
            }}
            noOptionsText={data ? 'No category matches' : 'Loading categories…'}
            renderOption={(props, o) => {
                const {key, ...rest} = props as typeof props & {key: string};
                return (
                    <Box component="li" key={key} {...rest} sx={{display: 'block !important'}}>
                        <Typography variant="body2">{o.name}</Typography>
                        {o.trail && <Typography variant="caption" color="text.secondary">{o.trail}</Typography>}
                    </Box>
                );
            }}
            renderInput={params => (
                <TextField {...params} inputRef={input} placeholder="Search or jump to…"
                           slotProps={{input: {
                               ...params.InputProps,
                               startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>,
                               endAdornment: (
                                   <Typography variant="caption" color="text.secondary"
                                               sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.75, mr: 0.5}}>
                                       {isMac ? '⌘ K' : 'Ctrl K'}
                                   </Typography>
                               ),
                           }, htmlInput: {...params.inputProps, 'aria-label': 'Search or jump to a category'}}}/>
            )}
        />
    );
};
